import * as PopoverPrimitive from '@radix-ui/react-popover';

import { type PopoverContentProps, type PopoverProps, type PopoverTriggerProps } from './types';
import { cn } from '../../utils/cn';

// =============================================================================
// Popover
// =============================================================================

/**
 * Root component for popover functionality. Manages open/close state.
 *
 * @param children - PopoverTrigger and PopoverContent components.
 * @param defaultOpen - Initial open state for uncontrolled usage. Defaults to false.
 * @param open - Controlled open state.
 * @param onOpenChange - Callback fired when open state changes.
 *
 * @example
 * <Popover>
 *   <PopoverTrigger asChild>
 *     <button>Open</button>
 *   </PopoverTrigger>
 *   <PopoverContent>
 *     <p>Popover content</p>
 *   </PopoverContent>
 * </Popover>
 */
export const Popover: React.FC<PopoverProps> = ({ children, defaultOpen, onOpenChange, open }) => {
    return (
        <PopoverPrimitive.Root defaultOpen={defaultOpen} onOpenChange={onOpenChange} open={open}>
            {children}
        </PopoverPrimitive.Root>
    );
};

// =============================================================================
// PopoverTrigger
// =============================================================================

/**
 * The element that triggers the popover on click.
 *
 * @param asChild - When true, the trigger will be the child element instead of a wrapper button.
 * @param children - The trigger element(s).
 * @param className - Additional CSS classes to merge with default styles.
 *
 * @example
 * <PopoverTrigger asChild>
 *   <button>Open popover</button>
 * </PopoverTrigger>
 */
export const PopoverTrigger: React.FC<PopoverTriggerProps> = ({ asChild = false, children, className }) => {
    return (
        <PopoverPrimitive.Trigger asChild={asChild} className={cn(className)}>
            {children}
        </PopoverPrimitive.Trigger>
    );
};

// =============================================================================
// PopoverContent
// =============================================================================

/**
 * The content that appears inside the popover.
 *
 * @param children - The content to display.
 * @param className - Additional CSS classes to merge with default styles.
 * @param align - Alignment of the popover relative to the trigger. Defaults to "center".
 * @param side - Side of the trigger where the popover appears. Defaults to "bottom".
 * @param sideOffset - Distance in pixels from the trigger. Defaults to 4.
 * @param ...props - Additional props passed to the underlying Radix UI component.
 *
 * @example
 * <PopoverContent side="bottom" align="start">
 *   <p>Popover content here</p>
 * </PopoverContent>
 */
export const PopoverContent: React.FC<PopoverContentProps> = ({
    align = 'center',
    children,
    className,
    side = 'bottom',
    sideOffset = 4,
    ...props
}) => {
    return (
        <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
                align={align}
                className={cn(
                    'z-50 w-72 rounded-md border border-grey-dark bg-white p-4 shadow-md',
                    'data-[state=open]:animate-in data-[state=closed]:animate-out',
                    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                    'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                    'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
                    'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                    'outline-none',
                    className,
                )}
                side={side}
                sideOffset={sideOffset}
                {...props}
            >
                {children}
            </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
    );
};
